import BookingTimeout from '@/components/App/BookingTimeout';
import BookingHeader from '@/components/App/Header/BookingHeader';
import history from '@/history';
import { apiSdk } from '@/httpClient';
import { AppUrls, generateHotelDetailUrlWithFullInfo } from '@/urls';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { ModalUtils } from '@/contexts/ModalWrapper';
import {
  PreBookContactDetailRequest,
  PreBookPassengerGenderModel,
  PrebookHotelSummaryModel,
  PrebookLeadPaxModel,
  RoomSearchInfo,
} from '@/__generated';
import { useForm } from 'react-hook-form';
import HotelSummary from '@/components/App/BookingComponents/HotelSummary';
import ContactDetail from '@/components/App/BookingComponents/ContactDetail';
import PassengerDetail from '@/components/App/BookingComponents/PassengerDetail';
import BookingStep from '@/components/App/BookingComponents/BookingStep';
import PageLoading from '@/components/App/PageLoading';
import Spin from '@/components/Spin';
import { handleBookingNotFound, handleSubmitError } from '@/helpers/errorHandle';
import PartialBookedConfirmModal from '@/components/App/BookingComponents/PartialBookedConfirmModal';
import bookingService from '@/services/bookingService';
import GuestNumber from '@/components/App/BookingComponents/GuestNumber';
import { useGetUseRole } from '@/hooks/useGetUserRole';
import { Helmet } from 'react-helmet';
import EditableContactDetail from '@/components/App/BookingComponents/EditableContactDetail';
import AgentBookingInfo from '@/components/App/BookingComponents/AgentBookingInfo';
import SpecialRequests from '@/components/App/BookingComponents/SpecialRequests';

type Props = {};
const createDefaultPassengerGenders = (rooms: RoomSearchInfo[]): PreBookPassengerGenderModel[] => {
  return rooms.map(room => {
    if (room.noOfAdult) {
      if (room.noOfAdult <= 2) {
        return {
          roomIndex: room.roomIndex,
          maleCount: 1,
        };
      } else if (room.noOfAdult <= 4) {
        return {
          roomIndex: room.roomIndex,
          maleCount: 2,
        };
      } else {
        return {
          roomIndex: room.roomIndex,
          maleCount: 3,
        };
      }
    }

    return {
      roomIndex: room.roomIndex,
      maleCount: 1,
    };
  });
};

export default function BookingPage({}: Props) {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [submitLoading, setSubmitLoading] = React.useState<boolean>(false);
  const [displayPartialBookedModal, setDisplayPartialBookedModal] = React.useState(false);
  const [bookingId, setBookingId] = React.useState<string>();
  const [passengerGenders, setPassengerGenders] = React.useState<PreBookPassengerGenderModel[]>([]);
  const [bookingSummary, setBookingSummary] = React.useState<PrebookHotelSummaryModel>();
  const {
    register,
    handleSubmit,
    setFocus,
    control,
    reset,
    formState: { errors },
  } = useForm({});

  const history = useHistory();
  const { isAdmin, isBooking } = useGetUseRole();
  if (!isAdmin && !isBooking) {
    history.push(AppUrls.Page401);
    return null;
  }

  const getHotelBookingSummary = (sId: string) => {
    setLoading(true);
    apiSdk.BookingService.getPrebookHotelSummary({ sessionId: sId })
      .then(res => {
        if (!res.sessionExpired && res.selectedHotel && (res.ttl || 0) > 0) {
          setBookingSummary(res);
          setPassengerGenders(res.passengerGenders?.length ? res.passengerGenders : createDefaultPassengerGenders(res.rooms || []));
          reset({
            leadPassenger: res.leadPassenger,
            contactDetail: res.contactDetail,
          });
        } else {
          handleBookingNotFound();
        }
      })
      .catch(() => {
        handleBookingNotFound();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    if (sessionId) {
      getHotelBookingSummary(sessionId);
    } else {
      history.push(AppUrls.HomePage);
    }
  }, [sessionId]);

  React.useEffect(() => {
    const firstError = Object.keys(errors).reduce((field: any, a) => {
      return !!errors[field] ? field : a;
    }, null);

    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);

  const onTimeout = () => {
    handleBookingNotFound();
  };

  const onHold = (leadPassenger: PrebookLeadPaxModel, contactDetail: PreBookContactDetailRequest, data: any) => {
    setSubmitLoading(true);
    apiSdk.BookingService.setPrebookPaxInfo({
      body: {
        sessionId: sessionId,
        ...data,
        contactDetail: contactDetail,
        leadPassenger: leadPassenger,
        passengerGenders: passengerGenders,
      },
    })
      .then(res => {
        if (res.sessionExpired) {
          handleBookingNotFound();
          setSubmitLoading(false);
        } else if (res.success) {
          apiSdk.BookingService.holdBooking({ sessionId: sessionId })
            .then(res => {
              const handlePartialBooking = () => {
                setDisplayPartialBookedModal(true);
                setBookingId(res.bookingId);
              };
              bookingService.handleCompleteBooking(res, handlePartialBooking);
            })
            .catch(handleSubmitError)
            .finally(() => {
              setSubmitLoading(false);
            });
        } else {
          setSubmitLoading(false);
          ModalUtils.error({
            title: 'Error!',
            content: <span>{res.message}</span>,
          });
        }
      })
      .catch(err => {
        handleSubmitError(err);
        setSubmitLoading(false);
      })
      .finally(() => {});
  };

  const goToPayment = (leadPassenger: PrebookLeadPaxModel, contactDetail: PreBookContactDetailRequest, data: any) => {
    setSubmitLoading(true);
    apiSdk.BookingService.setPrebookPaxInfo({
      body: { sessionId: sessionId, ...data, contactDetail: contactDetail, leadPassenger: leadPassenger, passengerGenders: passengerGenders },
    })
      .then(res => {
        if (res.sessionExpired) {
          handleBookingNotFound();
        } else if (res.success) {
          history.push(AppUrls.__createBookingPaymentUrl(sessionId));
        } else {
          ModalUtils.error({
            title: 'Error!',
            content: <span>{res.message}</span>,
          });
        }
      })
      .catch(handleSubmitError)
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  const onSubmit = async (data: any, event: React.BaseSyntheticEvent | null | undefined) => {
    const isOnHoldSubmit = (event?.nativeEvent as any)?.submitter?.id === 'on-hold';
    let { leadPassenger, contactDetail, ...otherData } = data;
    let leadPassengerInfo: PrebookLeadPaxModel = {
      ...data.leadPassenger,
      phoneCode: data.leadPassenger?.phoneCode?.value || data.leadPassenger?.phoneCode,
      countryCode: data.leadPassenger?.countryCode?.value || data.leadPassenger?.countryCode,
    };
    const contactDetailInfo = {
      ...data.contactDetail,
      phoneCode: data.contactDetail?.phoneCode?.value || data.contactDetail?.phoneCode,
    };
    if (isOnHoldSubmit) {
      onHold(leadPassengerInfo, contactDetailInfo, otherData);
    } else {
      goToPayment(leadPassengerInfo, contactDetailInfo, otherData);
    }
  };

  const hotelDetailUrl = React.useMemo(() => {
    if (bookingSummary && bookingSummary.selectedHotel) {
      return generateHotelDetailUrlWithFullInfo(
        bookingSummary.selectedHotel?.hotelName || '',
        bookingSummary.selectedHotel?.hotelCode,
        bookingSummary.checkinDate,
        bookingSummary.checkoutDate,
        bookingSummary.boardType,
        bookingSummary.rooms || [],
      );
    }
    return null;
  }, [bookingSummary]);

  if (loading) {
    return <PageLoading />;
  }

  if (!bookingSummary) {
    return null;
  }

  return (
    <div className="mb-50">
      <Helmet>
        <title>{`Guest Information - ${bookingSummary.sessionId}`}</title>
      </Helmet>
      <BookingHeader isSticky />
      <div className="header-margin" />
      <BookingTimeout ttl={bookingSummary.ttl || 0} onTimeout={onTimeout} />
      {displayPartialBookedModal && bookingId ? (
        <PartialBookedConfirmModal defaultConfirm={true} bookingId={bookingId} onClose={() => setDisplayPartialBookedModal(false)} />
      ) : null}

      <div className="container mt-20">
        <Spin loading={submitLoading}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <BookingStep activeStep={1} />
            <div className="row x-gap-50 mt-20 y-gap-20">
              <div className="col-xl-7 col-lg-8 mt-30 col-md-12 col-xs-12">
                <EditableContactDetail control={control} errors={errors} register={register} />
                <PassengerDetail control={control} errors={errors} register={register} />
                <GuestNumber
                  bookingSummary={bookingSummary}
                  passengerGenders={passengerGenders}
                  onPassengerGendersChange={v => setPassengerGenders(v)}
                />
                <AgentBookingInfo control={control} errors={errors} register={register} />
                <SpecialRequests register={register} bookingSummary={bookingSummary} />

                {hotelDetailUrl ? (
                  <div className="mt-30 sm:d-none md:d-none">
                    <Link to={hotelDetailUrl} className="text-red-1 fw-500 text-14 underline  items-center">
                      <FontAwesomeIcon icon={faChevronLeft} className="mr-5" />
                      Select other rooms?
                    </Link>
                  </div>
                ) : null}
              </div>
              <div className="col-xl-5 col-lg-4 col-md-12 col-xs-12 ">
                <HotelSummary bookingSummary={bookingSummary} showSubmitButtons />
              </div>
            </div>
          </form>
        </Spin>
      </div>
    </div>
  );
}
